/**
 * Component: sa-review
 * Description: Review Carousel Section
 */
if (!customElements.get('sa-review')) {
  class SaReview extends HTMLElement {
    constructor() {
      super();
      this.handleClickPrev = this.handleClickPrev.bind(this);
      this.handleClickNext = this.handleClickNext.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleResize = this.handleResize.bind(this);
      
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);

      this.currentIndex = 0;
      this.intervalId = null;
      this.isHovering = false;
      
      this.touchStartX = 0;
      this.touchStartY = 0;
      
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragStartIndex = 0;
      this.dragBaseTranslate = 0;
      this.dragStep = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('.sa-review__track');
      this.items = this.querySelectorAll('.sa-review__item');
      this.prevBtn = this.querySelector('.sa-review__btn--prev');
      this.nextBtn = this.querySelector('.sa-review__btn--next');
      
      if (!this.track || this.items.length === 0) return;

      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      this.autoplay=false;
      this.interval = parseInt(this.getAttribute('data-interval')) || 3000;

      this.initCarousel();
      this.addEventListeners();
      
      if (this.autoplay) {
        this.startAutoPlay();
      }
      
      console.log('SaReview loaded');
    }

    disconnectedCallback() {
      this.stopAutoPlay();
      this.removeEventListeners();
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    }

    initCarousel() {
      // Use requestAnimationFrame to ensure styles are applied and dimensions are correct
      requestAnimationFrame(() => {
        this.updateDimensions();
        this.updatePosition();
      });
    }

    updateDimensions() {
      const containerWidth = this.querySelector('.sa-review__carousel').offsetWidth;
      const itemWidth = this.items[0].offsetWidth;
      const gap = parseInt(window.getComputedStyle(this.track).gap) || 0;
      const step = itemWidth + gap;
      
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        this.visibleItems = 1;
        this.maxIndex = Math.max(0, this.items.length - 1);
      } else {
        this.visibleItems = Math.floor((containerWidth + gap) / step);
        if (this.visibleItems < 1) this.visibleItems = 1;
        this.maxIndex = Math.max(0, this.items.length - this.visibleItems);
      }
      
      // Reset index if out of bounds after resize
      if (this.currentIndex > this.maxIndex) {
        this.currentIndex = this.maxIndex;
      }
    }

    addEventListeners() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handleClickPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleClickNext);
      
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      window.addEventListener('resize', this.handleResize);

      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.handleTouchEnd);
      
      this.track.addEventListener('mousedown', this.handleMouseDown);
    }

    removeEventListeners() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handleClickPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleClickNext);
      
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      window.removeEventListener('resize', this.handleResize);

      this.track.removeEventListener('touchstart', this.handleTouchStart);
      this.track.removeEventListener('touchend', this.handleTouchEnd);
      
      this.track.removeEventListener('mousedown', this.handleMouseDown);
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
      this.stopAutoPlay();
    }

    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      
      const diffX = touchEndX - this.touchStartX;
      const diffY = touchEndY - this.touchStartY;
      
      // Horizontal swipe detection (threshold: 50px)
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0) {
          this.prev();
        } else {
          this.next();
        }
      }

      if (this.autoplay) {
        this.startAutoPlay();
      }
    }

    handleMouseDown(e) {
      // Only respond to primary button
      if (e.button !== 0) return;
      
      e.preventDefault();
      
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragStartIndex = this.currentIndex;
      this.isDragging = false;
      
      // Compute dimensions for this drag session
      const containerWidth = this.querySelector('.sa-review__carousel').offsetWidth;
      const itemWidth = this.items[0].offsetWidth;
      const gap = parseInt(window.getComputedStyle(this.track).gap) || 0;
      this.dragStep = itemWidth + gap;
      
      const isMobile = window.innerWidth <= 768;
      let offset = 0;
      if (isMobile) {
        offset = (containerWidth - itemWidth) / 2;
      }
      this.dragBaseTranslate = -(this.currentIndex * this.dragStep) + offset;
      
      // Kill transition for instant mouse-follow
      this.track.style.transition = 'none';
      
      this.stopAutoPlay();
      
      // Bind document-level to catch drag outside track
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    }

    handleMouseMove(e) {
      const diffX = this.dragStartX - e.clientX;
      const diffY = this.dragStartY - e.clientY;
      
      // Small threshold to distinguish drag from click
      if (!this.isDragging) {
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 5) {
          this.isDragging = true;
        } else {
          return;
        }
      }
      
      // Real-time follow
      const translate = this.dragBaseTranslate - diffX;
      this.track.style.transform = `translateX(${translate}px)`;
    }

    handleMouseUp(e) {
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      
      // Restore transition for snap animation
      this.track.style.transition = '';
      
      if (!this.isDragging) {
        if (this.autoplay) {
          this.startAutoPlay();
        }
        return;
      }
      
      const diffX = this.dragStartX - e.clientX;
      
      // Snap to nearest card
      const cardsToMove = Math.round(diffX / this.dragStep);
      let newIndex = this.dragStartIndex + cardsToMove;
      
      // Clamp to valid range
      newIndex = Math.max(0, Math.min(this.maxIndex, newIndex));
      
      this.currentIndex = newIndex;
      this.updatePosition();
      this.isDragging = false;
      
      if (this.autoplay) {
        this.startAutoPlay();
      }
    }

    handleResize() {
      // Debounce could be added here
      this.updateDimensions();
      this.updatePosition();
    }

    handleClickPrev(e) {
      e.preventDefault();
      this.prev();
    }

    handleClickNext(e) {
      e.preventDefault();
      this.next();
    }

    handleMouseEnter() {
      this.isHovering = true;
      this.stopAutoPlay();
    }

    handleMouseLeave() {
      this.isHovering = false;
      if (this.autoplay) {
        this.startAutoPlay();
      }
    }

    next() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++;
      } else {
        // Loop back to start
        this.currentIndex = 0;
      }
      this.updatePosition();
    }

    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else {
        // Loop to end
        this.currentIndex = this.maxIndex;
      }
      this.updatePosition();
    }

    updatePosition() {
      const containerWidth = this.querySelector('.sa-review__carousel').offsetWidth;
      const itemWidth = this.items[0].offsetWidth;
      const gap = parseInt(window.getComputedStyle(this.track).gap) || 0;
      const step = itemWidth + gap;
      
      const isMobile = window.innerWidth <= 768;
      let offset = 0;

      if (isMobile) {
        offset = (containerWidth - itemWidth) / 2;
      }
      
      const translate = -(this.currentIndex * step) + offset;
      this.track.style.transform = `translateX(${translate}px)`;
    }

    startAutoPlay() {
      if (this.intervalId) clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        if (!this.isHovering) {
          this.next();
        }
      }, this.interval);
    }

    stopAutoPlay() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  }

  customElements.define('sa-review', SaReview);
}
